import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import type { ReactNode } from "react";
import { ProvedorSessao, useSessao } from "./estado/sessao";
import { Login } from "./paginas/Login";
import { Dashboard } from "./paginas/Dashboard";
import { Clientes } from "./paginas/Clientes";
import { ClienteDetalhe } from "./paginas/ClienteDetalhe";
import { Templates } from "./paginas/Templates";
import { TemplateDetalhe } from "./paginas/TemplateDetalhe";
import { Calendario } from "./paginas/Calendario";
import { Vistorias } from "./paginas/Vistorias";
import { VistoriaDetalhe } from "./paginas/VistoriaDetalhe";
import { Equipe } from "./paginas/Equipe";
import { Mais } from "./paginas/Mais";
import { Perfil } from "./paginas/Perfil";
import { Tarefas } from "./paginas/Tarefas";
import { AdministracaoGeral } from "./paginas/AdministracaoGeral";
import { Landing } from "./paginas/Landing";

/** Segura a renderização até a sessão ser verificada, evitando o "flash" da tela de login. */
function Protegida({ children }: { children: ReactNode }) {
  const { usuario, carregando } = useSessao();
  if (carregando) return <div className="container">Carregando…</div>;
  if (!usuario) return <Navigate to="/entrar" replace />;
  return <>{children}</>;
}

/** Página pública (login): quem já está logado é mandado direto pro painel. */
function Publica({ children }: { children: ReactNode }) {
  const { usuario, carregando } = useSessao();
  if (carregando) return <div className="container">Carregando…</div>;
  if (usuario) return <Navigate to="/painel" replace />;
  return <>{children}</>;
}

function SomenteAdmin({ children }: { children: ReactNode }) {
  const { usuario } = useSessao();
  if (usuario?.papel !== "ADMIN") return <Navigate to="/painel" replace />;
  return <>{children}</>;
}

function SomenteSuperAdmin({ children }: { children: ReactNode }) {
  const { usuario } = useSessao();
  if (usuario?.papel !== "SUPER_ADMIN") return <Navigate to="/painel" replace />;
  return <>{children}</>;
}

function Inicio() {
  const { usuario } = useSessao();
  return usuario?.papel === "SUPER_ADMIN" ? <Navigate to="/admin-geral" replace /> : <Dashboard />;
}

export function App() {
  return (
    <ProvedorSessao>
      <BrowserRouter>
        <Routes>
          <Route
            path="/entrar"
            element={
              <Publica>
                <Login />
              </Publica>
            }
          />
          <Route
            path="/"
            element={
              <Publica><Landing /></Publica>
            }
          />
          <Route path="/painel" element={<Protegida><Inicio /></Protegida>} />
          <Route path="/admin-geral" element={<Protegida><SomenteSuperAdmin><AdministracaoGeral /></SomenteSuperAdmin></Protegida>} />
          <Route
            path="/clientes"
            element={
              <Protegida>
                <Clientes />
              </Protegida>
            }
          />
          <Route
            path="/clientes/:id"
            element={
              <Protegida>
                <ClienteDetalhe />
              </Protegida>
            }
          />
          <Route
            path="/templates"
            element={
              <Protegida>
                <SomenteAdmin><Templates /></SomenteAdmin>
              </Protegida>
            }
          />
          <Route
            path="/templates/:id"
            element={
              <Protegida>
                <SomenteAdmin><TemplateDetalhe /></SomenteAdmin>
              </Protegida>
            }
          />
          <Route
            path="/agenda"
            element={
              <Protegida>
                <Calendario />
              </Protegida>
            }
          />
          <Route
            path="/vistorias"
            element={
              <Protegida>
                <Vistorias />
              </Protegida>
            }
          />
          <Route
            path="/vistorias/:id"
            element={
              <Protegida>
                <VistoriaDetalhe />
              </Protegida>
            }
          />
          <Route path="/vistorias-realizadas" element={<Navigate to="/vistorias" replace />} />
          <Route path="/nao-conformidades" element={<Navigate to="/vistorias" replace />} />
          <Route
            path="/equipe"
            element={
              <Protegida>
                <SomenteAdmin><Equipe /></SomenteAdmin>
              </Protegida>
            }
          />
          <Route
            path="/tarefas"
            element={<Protegida><Tarefas /></Protegida>}
          />
          <Route
            path="/perfil"
            element={<Protegida><Perfil /></Protegida>}
          />
          <Route
            path="/mais"
            element={
              <Protegida>
                <Mais />
              </Protegida>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </ProvedorSessao>
  );
}
